import markerSDK from '@marker.io/browser';
import { FlagIcon } from '@orn/shared-web/icons';
import { cx } from '@orn/shared-web/utils/cx';
import * as Sentry from '@sentry/react';
import * as React from 'react';
import { createPortal } from 'react-dom';

import { MOBILE_TOP_BAR_HEIGHT_PX } from '../components/navigation/dimensions';
import { useDeviceBreakpoints } from '../hooks/useDeviceBreakpoints';
import { getOptionalEnv } from '../lib/getEnv';

import { useAuthState } from './AuthProvider';

const MARKER_PROJECT_ID = getOptionalEnv('VITE_MARKER_PROJECT_ID');
const MARKER_LOAD_MAX_ATTEMPTS = 3;
const MARKER_LOAD_RETRY_DELAY_MS = 1_000;

export async function loadMarkerWidget(
  projectId: string,
  retryDelayMs: number = MARKER_LOAD_RETRY_DELAY_MS
): Promise<boolean> {
  let lastError: unknown;

  for (let attempt = 1; attempt <= MARKER_LOAD_MAX_ATTEMPTS; attempt += 1) {
    try {
      await markerSDK.loadWidget({ project: projectId });
      return true;
    } catch (error) {
      lastError = error;
      if (attempt < MARKER_LOAD_MAX_ATTEMPTS) {
        await new Promise<void>((resolve) => setTimeout(resolve, retryDelayMs));
      }
    }
  }

  Sentry.captureException(new Error('Marker.io widget failed to load after retries', { cause: lastError }), {
    tags: {
      marker: true,
    },
    contexts: {
      marker: {
        attempts: MARKER_LOAD_MAX_ATTEMPTS,
      },
    },
    level: 'warning',
  });
  return false;
}

interface IMarkerContext {
  capture: () => void;
}

const MarkerContext = React.createContext<IMarkerContext>({ capture: () => {} });

export const useMarker = () => React.useContext(MarkerContext);

type IMarkerProviderProps = React.PropsWithChildren;

/**
 * Container element for the custom report button, rendered as a portal into
 * document.body with data-react-aria-top-layer so it stays interactive when
 * React Aria modals/slideouts are open.
 */
function useMarkerPortal() {
  const [container, setContainer] = React.useState<HTMLDivElement | null>(null);

  React.useEffect(() => {
    const el = document.createElement('div');
    el.dataset.reactAriaTopLayer = 'true';
    document.body.appendChild(el);
    setContainer(el);
    return () => {
      document.body.removeChild(el);
    };
  }, []);

  return container;
}

const MarkerProvider = ({ children }: IMarkerProviderProps) => {
  const { user } = useAuthState();
  const { isMobile } = useDeviceBreakpoints();
  const portalContainer = useMarkerPortal();
  const [isWidgetReady, setIsWidgetReady] = React.useState(!!window.Marker);

  const capture = React.useCallback(() => {
    if (window.Marker) {
      void window.Marker.capture('fullscreen');
    }
  }, []);

  // Initialize the widget once (only if project ID is configured).
  // Uses window.Marker as the source of truth rather than a React ref,
  // because React 18 Strict Mode double-mounts break the ref pattern.
  React.useEffect(() => {
    if (window.Marker || !MARKER_PROJECT_ID) {
      return;
    }
    let cancelled = false;

    const hideDefaultButton = () => {
      const feedbackButton = document.getElementById('feedback-button');
      if (feedbackButton) {
        feedbackButton.style.setProperty('display', 'none', 'important');
      }
    };

    void loadMarkerWidget(MARKER_PROJECT_ID).then((loaded) => {
      if (!loaded || cancelled) {
        return;
      }

      // Hide the default Marker.io button via CSS — the SDK's hide()
      // method puts the widget in a state that ignores capture() calls.
      hideDefaultButton();

      // Re-hide whenever Marker.io tries to show its default button
      // (e.g. after a capture flow ends).
      window.Marker.on('show', hideDefaultButton);

      // Tag the Marker.io container so React Aria's ariaHideOutside
      // keeps it interactive when modals/slideouts are open (avoids inert).
      const markerContainer = document.querySelector<HTMLElement>('div.marker-app');
      if (markerContainer) {
        markerContainer.dataset.reactAriaTopLayer = 'true';
      }

      setIsWidgetReady(true);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  // Update reporter info when user changes or widget becomes ready.
  // The isWidgetReady dependency ensures reporter is set even if the user
  // was already logged in when loadWidget resolved.
  React.useEffect(() => {
    if (!isWidgetReady) {
      return;
    }
    if (user) {
      window.Marker.setReporter({
        email: user.email ?? undefined,
        fullName: user.name ?? undefined,
      });
    } else {
      window.Marker.clearReporter();
    }
  }, [user, isWidgetReady]);

  return (
    <MarkerContext.Provider value={{ capture }}>
      {children}
      {MARKER_PROJECT_ID && portalContainer && isWidgetReady
        ? createPortal(
            <ReportButton
              onClick={capture}
              edge={isMobile ? MOBILE_REPORT_BUTTON_EDGE : REPORT_BUTTON_EDGE}
              align={isMobile ? MOBILE_REPORT_BUTTON_ALIGN : REPORT_BUTTON_ALIGN}
            />,
            portalContainer
          )
        : null}
    </MarkerContext.Provider>
  );
};

/** Breathing room between the mobile top bar and anything hung below it. */
const REPORT_BUTTON_TOP_BAR_GAP_PX = 12;

/**
 * Distance from the viewport top that clears the mobile top bar, published as a
 * custom property so the offset tracks `MOBILE_TOP_BAR_HEIGHT_PX` instead of
 * restating it as a Tailwind step that silently drifts when the nav resizes.
 */
const REPORT_BUTTON_TOP_STYLE = {
  '--report-button-top': `${MOBILE_TOP_BAR_HEIGHT_PX + REPORT_BUTTON_TOP_BAR_GAP_PX}px`,
} as React.CSSProperties;

/**
 * Which wall the report button clings to. `left` / `right` turn it into a
 * vertical tab flush to that wall; `top` clears the mobile top bar so it never
 * hides the nav.
 */
const REPORT_BUTTON_EDGE_CLASSES = {
  top: 'top-(--report-button-top)',
  bottom: 'bottom-3',
  // The 180deg turn swaps the box's sides, so the corners squared against the
  // wall are the ones named `r` here.
  left: 'left-0 rounded-r-none [writing-mode:vertical-rl] rotate-180',
  right: 'right-0 rounded-r-none [writing-mode:vertical-rl]',
} satisfies Record<string, string>;

type ReportButtonEdge = keyof typeof REPORT_BUTTON_EDGE_CLASSES;

/**
 * Where along that wall it sits — `start` is the wall's left/top end, `end` its
 * right/bottom end, `center` the midpoint. The vertical `end` rides above the
 * assistant composer.
 */
const REPORT_BUTTON_ALIGN_CLASSES = {
  horizontal: { start: 'left-5', center: 'left-1/2 -translate-x-1/2', end: 'right-5' },
  vertical: { start: 'top-(--report-button-top)', center: 'top-1/2 -translate-y-1/2', end: 'bottom-24' },
} satisfies Record<string, Record<string, string>>;

type ReportButtonAlign = keyof (typeof REPORT_BUTTON_ALIGN_CLASSES)['horizontal'];

const isVerticalEdge = (edge: ReportButtonEdge) => edge === 'left' || edge === 'right';

/** Below `md` the bottom-right corner belongs to the assistant's send button. */
const MOBILE_REPORT_BUTTON_EDGE: ReportButtonEdge = 'right';
const MOBILE_REPORT_BUTTON_ALIGN: ReportButtonAlign = 'center';
const REPORT_BUTTON_EDGE: ReportButtonEdge = 'bottom';
const REPORT_BUTTON_ALIGN: ReportButtonAlign = 'end';

interface IReportButtonProps {
  onClick: () => void;
  edge: ReportButtonEdge;
  align: ReportButtonAlign;
}

const ReportButton = ({ onClick, edge, align }: IReportButtonProps) => {
  const isVertical = isVerticalEdge(edge);

  return (
    <button
      type="button"
      onClick={onClick}
      style={REPORT_BUTTON_TOP_STYLE}
      className={cx(
        'bg-error-solid ring-error_subtle hover:bg-error-solid_hover hover:ring-error shadow-xs z-9999 fixed flex cursor-pointer items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium text-white ring-1 transition-colors',
        REPORT_BUTTON_EDGE_CLASSES[edge],
        REPORT_BUTTON_ALIGN_CLASSES[isVertical ? 'vertical' : 'horizontal'][align]
      )}
    >
      {/* Vertical writing mode turns the glyphs but leaves the SVG upright. */}
      <FlagIcon className={cx('size-3.5 shrink-0', isVertical && 'rotate-90')} />
      Report
    </button>
  );
};

export default MarkerProvider;
